import { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import InterestFreeOption from './InterestFreeOption'
import {
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
} from 'lib/payment/paymentUtils'
import { KLARNA_PAYMENT_TYPE } from 'constants/payment'
import { connect } from 'react-redux'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'

const HowToUseSection = styled.div`
  margin-inline: auto;
  line-height: 1.8;
  max-width: ${rem(680)};
`

const PaymentOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  isOpen?: boolean
  onClose?: () => void
  currency: string
  offer?: App.Offer
}

function InterestFreeLearnMoreModal(props: Props) {
  const { isOpen, onClose, currency, offer } = props
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)

  const payOver4Label = intl.formatMessage({
    id: 'K5WRrOlpnfNVzO9ycgZy',
    defaultMessage: 'Pay over 4 interest-free instalments',
    description: 'Title for interest-free payment option with 4 instalments',
  })

  const klarnaPayAlt = intl.formatMessage({
    id: 'a_jEVA9Qap-HR_em4FJs',
    defaultMessage: 'Klarna Pay',
    description: 'Alt text for Klarna payment provider logo in the payment section',
  })

  const products = [
    {
      id: KLARNA_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt={klarnaPayAlt} height="40" />,
      brand: 'Klarna',
      title: payOver4Label,
      content: intl.formatMessage({
        id: 'rDfAs856966Zxe_tpoNX',
        defaultMessage: 'For purchases under {amount}',
        description: 'Klarna payment option limit description during the checkout and payment process',
      }, { amount: formatRoundedAmountWithDollarType(2500, currency, currentRegionCode) }),
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaAvailabilityRange(p.price))),
    },
  ]

  const modalTitle = intl.formatMessage({
    id: 'rpb0XLQ21O09TzHjSafz',
    defaultMessage: 'Interest free payment options',
    description: 'Heading title for the interest-free payment options modal',
  })

  const modalSubtitle = intl.formatMessage({
    id: 'hTsYeNd34RWc236LgyAo',
    defaultMessage: 'Select from the following interest free payment options in checkout.',
    description: 'Subtitle explaining user can select from available interest-free payment options',
  })

  const howToUseHeading = intl.formatMessage({
    id: '5k4zvpvWCpE5bbwULvdF',
    defaultMessage: 'How to use this service',
    description: 'Heading for instructions on using interest-free payment service',
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
    >
      <VerticalSpacer gap={20}>
        <PaymentOptionSection>
          {products.map(product => {
            if (product.isAvailable(currency, offer)) {
              return (<InterestFreeOption
                key={product.id}
                product={product}
              />)
            }
            return null
          })}
        </PaymentOptionSection>
        <HowToUseSection>
          <Heading variant="heading6">{howToUseHeading}</Heading>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'nBo-6gDoaUPotvg-wqTx',
              defaultMessage: 'Select your Escape and checkout as normal. In the checkout select the interest free payment option and continue from there.',
              description: 'Instructions on how to use interest free payment option',
            })}
          </BodyText>
        </HowToUseSection>
      </VerticalSpacer>
    </Modal>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(InterestFreeLearnMoreModal)
